import React from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  TextField,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { PRICING, calculateGstComponents } from '../../../config';
import { useSuperDuper } from '../context/SuperDuperContext';

const GenerateReceiptTab = () => {
  const {
    receiptForm,
    isGeneratingReceipt,
    handleReceiptFormChange,
    handleGenerateReceipt
  } = useSuperDuper();

  return (
    <>
      <Box sx={{ mt: { xs: 1, sm: 2, md: 4 } }}>
        <Typography
          variant="h4"
          sx={{ mb: { xs: 2, sm: 4 }, fontSize: { xs: '1.35rem', sm: '2.125rem' } }}
        >
          Generate Receipt
        </Typography>
        
        <Card sx={{ maxWidth: 800, mx: 'auto' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 }, '&:last-child': { pb: { xs: 2, sm: 3, md: 4 } } }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Receipt Information
            </Typography>
            
            <Grid container spacing={3}>
              {/* Amount - Required */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Amount *"
                  type="number"
                  value={receiptForm.amount}
                  onChange={handleReceiptFormChange('amount')}
                  placeholder="Enter total amount"
                  inputProps={{ step: '0.01', min: '0' }}
                  helperText="GST will be calculated automatically"
                />
              </Grid>
              
              {/* Receipt Date - Required */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Date of Invoice *"
                  type="date"
                  value={receiptForm.receiptDate}
                  onChange={handleReceiptFormChange('receiptDate')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              
              {/* Customer Name - Optional */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Customer Name"
                  value={receiptForm.customerName}
                  onChange={handleReceiptFormChange('customerName')}
                  placeholder="Enter customer name (optional)"
                />
              </Grid>
              
              {/* Customer Email - Required */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Customer Email *"
                  type="email"
                  value={receiptForm.customerEmail}
                  onChange={handleReceiptFormChange('customerEmail')}
                  placeholder="Enter customer email"
                  helperText="Required for receipt generation"
                />
              </Grid>
              
              {/* Phone - Optional */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={receiptForm.customerPhone}
                  onChange={handleReceiptFormChange('customerPhone')}
                  placeholder="Enter phone number (optional)"
                />
              </Grid>
              
              {/* Plan Name - Optional */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Plan Name"
                  value={receiptForm.planName}
                  onChange={handleReceiptFormChange('planName')}
                  placeholder="Enter plan name (optional)"
                />
              </Grid>
              
              {/* Receipt Number */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Receipt No."
                  value={receiptForm.receiptNo}
                  onChange={handleReceiptFormChange('receiptNo')}
                  placeholder="Enter receipt number (optional)"
                />
              </Grid>
              
              {/* Payment ID */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Payment ID"
                  value={receiptForm.paymentId}
                  onChange={handleReceiptFormChange('paymentId')}
                  placeholder="Enter payment ID (optional)"
                />
              </Grid>
              
              {/* Subscription ID */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Subscription ID"
                  value={receiptForm.subscriptionId}
                  onChange={handleReceiptFormChange('subscriptionId')}
                  placeholder="Enter subscription ID (optional)"
                />
              </Grid>
            </Grid>
            
            {/* GST Preview */}
            {receiptForm.amount && !isNaN(parseFloat(receiptForm.amount)) && parseFloat(receiptForm.amount) > 0 && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>GST Calculation Preview:</Typography>
                {(() => {
                  const amount = parseFloat(receiptForm.amount);
                  const gstComponents = calculateGstComponents(amount);
                  return (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2">Base Amount: ₹{gstComponents.baseAmount.toFixed(2)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2">GST ({PRICING.GST.RATE}%): ₹{gstComponents.gstAmount.toFixed(2)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Total: ₹{gstComponents.totalAmount.toFixed(2)}</Typography>
                      </Grid>
                    </Grid>
                  );
                })()}
              </Box>
            )}
            
            {/* Generate Button */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleGenerateReceipt}
                disabled={isGeneratingReceipt || !receiptForm.amount || !receiptForm.customerEmail}
                startIcon={isGeneratingReceipt ? <CircularProgress size={20} /> : <ReceiptIcon />}
                sx={{ minWidth: { xs: '100%', sm: 200 } }}
              >
                {isGeneratingReceipt ? 'Generating...' : 'Generate Receipt'}
              </Button>
            </Box>
            
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2, color: 'text.secondary' }}>
              * Required fields. GST will be calculated automatically based on the amount.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default GenerateReceiptTab;
